import type { StepProps } from "@/types/crm";
import React from "react";
import { Trophy, XCircle } from "lucide-react";
import type { StageType } from "@/hooks/usePipelinesSettings";

export type SortOrder = 'newest' | 'oldest';

interface ExtendedStepProps extends StepProps {
  stageType?: StageType;
  width?: number;
  sortOrder?: SortOrder;
  onSortToggle?: () => void;
}

const Step: React.FC<ExtendedStepProps> = ({ title, amount, count, stageType = 'normal', width, sortOrder = 'newest', onSortToggle }) => {
  const getContainerStyle = () => {
    switch (stageType) {
      case 'won':
        return 'bg-[#ECFDF5] border border-[#BBF7D0] rounded-xl dark:bg-green-900/30 dark:border-green-600/40';
      case 'lost':
        return 'bg-red-50 border border-red-200 rounded-xl dark:bg-red-900/30 dark:border-red-600/40';
      default:
        return 'bg-transparent border border-transparent';
    }
  };

  const getHeaderStyle = () => {
    switch (stageType) {
      case 'won':
        return 'border-b border-[#BBF7D0] dark:border-green-500/50 pb-2';
      case 'lost':
        return 'border-b border-red-200 dark:border-red-500/50 pb-2';
      default:
        return 'border-b border-border dark:border-[#3F3F464D] pb-2';
    }
  };

  const getIcon = () => {
    switch (stageType) {
      case 'won':
        return <Trophy className="w-4 h-4 text-[#16A34A] dark:text-green-400" />;
      case 'lost':
        return <XCircle className="w-4 h-4 text-red-500 dark:text-red-400" />;
      default:
        return null;
    }
  };

  const getTitleStyle = () => {
    switch (stageType) {
      case 'won':
        return 'text-[#16A34A] dark:text-green-300';
      case 'lost':
        return 'text-red-600 dark:text-red-300';
      default:
        return 'text-foreground dark:text-neutral-50';
    }
  };

  return (
    <div
      className={`rounded-xl px-4 py-4 flex flex-col gap-3 text-foreground dark:text-neutral-50 transition-colors duration-200 ${getContainerStyle()}`}
      style={width ? { width: `${width}px` } : { width: '240px' }}
    >
      <div className={`flex justify-between items-center ${getHeaderStyle()}`}>
        <div className="flex items-center gap-2">
          {getIcon()}
          <span className={`text-lg font-semibold ${getTitleStyle()}`}>{title}</span>
        </div>
        <button
          type="button"
          aria-label={sortOrder === 'newest' ? 'Ordenar por mais antigas' : 'Ordenar por mais novas'}
          onClick={(e) => {
            e.stopPropagation();
            onSortToggle?.();
          }}
          className="p-1 hover:bg-[#F1F5F9] dark:hover:bg-white/10 rounded transition-colors duration-200"
          title={sortOrder === 'newest' ? 'Mais novas primeiro' : 'Mais antigas primeiro'}
        >
          <img
            src="/arrow-down.svg"
            alt="Toggle Arrow"
            className="transition-transform duration-300 dark:invert-0 invert opacity-60 dark:opacity-100"
            style={{ transform: sortOrder === 'oldest' ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </button>
      </div>
      <div className="flex justify-between items-center text-muted-foreground dark:text-neutral-300">
        <div className="flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-muted-foreground dark:text-neutral-50">
            <path
              d="M5.66626 1H8.33325C8.88905 1 9.33308 1.44425 9.33325 2V3.66699H12.3333C12.8892 3.66699 13.3333 4.11109 13.3333 4.66699V12C13.3333 12.5559 12.8892 13 12.3333 13H1.66626C1.11072 12.9998 0.666606 12.5565 0.66626 12.001L0.673096 4.66699C0.673096 4.10901 1.11269 3.66717 1.66626 3.66699H4.66626V2C4.66643 1.44436 5.11062 1.00017 5.66626 1ZM2.33325 4.33398C1.78249 4.33398 1.33325 4.78322 1.33325 5.33398V11.334C1.33343 11.8846 1.7826 12.334 2.33325 12.334H11.6663C12.2169 12.334 12.6661 11.8846 12.6663 11.334V5.33398C12.6663 4.78322 12.217 4.33398 11.6663 4.33398H2.33325ZM5.33325 3.66699H8.66626V1.66699H5.33325V3.66699Z"
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="0.666667"
            />
          </svg>
          <span className="text-sm">{count}</span>
        </div>
        <div className="flex items-center gap-2">
          <img src="/coin.svg" alt="Coin Icon" className="opacity-70 dark:opacity-100 dark:invert-0 invert-[0.4]" />
          <span className="text-sm font-medium text-foreground dark:text-neutral-50">{amount}</span>
        </div>
      </div>
    </div>
  );
};

export default Step;
